import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, Send, Plus, StickyNote, Calculator, LineChart, Loader2, Brain, X, MoveUpRight } from 'lucide-react';
import { apiAIFetch } from '../../api';
import SessionContextPicker from './SessionContextPicker';

/**
 * WhiteboardInput - AI-powered input bar for the whiteboard
 *
 * Uses Gemini 2.5 Pro with function calling to intelligently route
 * user natural language input to the appropriate whiteboard action.
 *
 * Golden Ratio Design System: φ = 1.618
 * Base: 1rem | Levels: 0.618rem, 1rem, 1.618rem, 2.618rem
 */

const suggestions = [
  {
    id: 'note',
    label: 'Add Note',
    icon: StickyNote,
    iconClass: 'note',
    description: 'Add a sticky note to the board'
  },
  {
    id: 'math',
    label: 'Add Math Equation',
    icon: Calculator,
    iconClass: 'math',
    description: 'Add a LaTeX math expression'
  },
  {
    id: 'graph',
    label: 'Add Graph',
    icon: LineChart,
    iconClass: 'graph',
    description: 'Add a Desmos graph'
  },
  {
    id: 'fbd',
    label: 'Add FBD',
    icon: MoveUpRight,
    iconClass: 'fbd',
    description: 'Add a free body diagram'
  }
];

const WhiteboardInput = ({
  onAddNote,
  onAddMath,
  onAddChart,
  onAddFbd,
  onAiMessage,
  injectedContext,
  onContextChange,
  currentPhase
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const hasContext = injectedContext?.active;

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleSuggestionClick = useCallback((suggestionId) => {
    switch (suggestionId) {
      case 'note':
        onAddNote?.();
        break;
      case 'math':
        onAddMath?.();
        break;
      case 'graph':
        onAddChart?.();
        break;
      case 'fbd':
        onAddFbd?.();
        break;
      default:
        break;
    }
    setIsOpen(false);
    setInputValue('');
  }, [onAddNote, onAddMath, onAddChart, onAddFbd]);

  // Handle context picker confirm
  const handleContextConfirm = useCallback((contextData) => {
    onContextChange?.({
      active: true,
      ...contextData
    });
    onAiMessage?.(`Session context loaded: ${contextData.sessionName}`);
  }, [onContextChange, onAiMessage]);

  // Handle clear context
  const handleClearContext = useCallback(() => {
    onContextChange?.(null);
  }, [onContextChange]);

  const handleSubmit = useCallback(async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    setIsLoading(true);
    setIsOpen(false);

    // Build request body, including context and phase if available
    const requestBody = { input: trimmedInput };

    // Include current session phase for phase-aware AI responses
    if (currentPhase) {
      requestBody.phase = currentPhase;
    }

    if (injectedContext?.active) {
      requestBody.context = {
        sessionId: injectedContext.sessionId,
        summary: injectedContext.summary,
        transcript: injectedContext.transcript,
        studentProfile: injectedContext.studentProfile
      };
    }

    try {
      const response = await apiAIFetch('/api/whiteboard/ai-action', 'whiteboard.ai_action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      const { action, args, neuro_technique } = data;

      switch (action) {
        case 'add_note':
          onAddNote?.(args.content, args.color);
          break;
        case 'add_math':
          onAddMath?.(args.expression);
          break;
        case 'add_graph':
          onAddChart?.(args.expression);
          break;
        case 'add_fbd':
          onAddFbd?.();
          break;
        case 'respond_text':
          // Pass neuro_technique for badge display
          onAiMessage?.(args.message, neuro_technique);
          break;
        default:
          console.warn('Unknown action:', action);
      }

      setInputValue('');
    } catch (error) {
      console.error('Error processing AI action:', error);
      onAiMessage?.('Sorry, something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, injectedContext, currentPhase, onAddNote, onAddMath, onAddChart, onAddFbd, onAiMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }, [handleSubmit]);

  const hasInput = inputValue.trim().length > 0;

  return (
    <div ref={containerRef} className="wb-ai-input-container">
      {/* Suggestions Panel */}
      <div className={`wb-ai-input-suggestions ${isOpen ? 'open' : ''}`}>
        <div className="wb-ai-input-suggestions-header">
          Quick Actions
        </div>
        <div className="wb-ai-input-suggestions-list">
          {suggestions.map((suggestion) => {
            const IconComponent = suggestion.icon;
            return (
              <button
                key={suggestion.id}
                className="wb-ai-input-suggestion-item"
                onClick={() => handleSuggestionClick(suggestion.id)}
              >
                <div className={`wb-ai-input-suggestion-icon ${suggestion.iconClass}`}>
                  <IconComponent />
                </div>
                <div className="wb-ai-input-suggestion-text">
                  <div className="wb-ai-input-suggestion-label">
                    {suggestion.label}
                  </div>
                  <div className="wb-ai-input-suggestion-desc">
                    {suggestion.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Session Context Picker */}
      {isPickerOpen && (
        <SessionContextPicker
          onConfirm={handleContextConfirm}
          onClose={() => setIsPickerOpen(false)}
          currentContext={injectedContext}
        />
      )}

      {/* Main Input Bar */}
      <div className={`wb-ai-input-bar ${isOpen ? 'focused' : ''}`}>
        {/* Brain Icon - Session Context Trigger */}
        <button
          className={`wb-context-trigger ${isPickerOpen ? 'active' : ''} ${hasContext ? 'has-context' : ''}`}
          onClick={() => setIsPickerOpen(!isPickerOpen)}
          title={hasContext ? 'Session context active - click to change' : 'Pull session context'}
        >
          <Brain />
        </button>

        <div className="wb-ai-input-icon">
          <Sparkles />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={hasContext ? 'Ask AI (with session context)...' : 'Ask AI or type \'/\' for commands...'}
          className="wb-ai-input-field"
          disabled={isLoading}
        />

        <div className="wb-ai-input-actions">
          {/* Clear context button - only show when context is active */}
          {hasContext && (
            <button
              className="wb-context-clear"
              onClick={handleClearContext}
              title="Clear session context"
            >
              <X />
              <span>Clear</span>
            </button>
          )}

          <button
            className="wb-ai-input-action-btn"
            onClick={() => setIsOpen(!isOpen)}
            title="Quick actions"
          >
            <Plus />
          </button>

          <button
            className={`wb-ai-input-send-btn ${hasInput ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={handleSubmit}
            disabled={!hasInput || isLoading}
            title="Send"
          >
            {isLoading ? <Loader2 /> : <Send />}
          </button>
        </div>
      </div>

      {/* Helper Text */}
      <div className={`wb-ai-input-helper ${isOpen ? 'visible' : ''}`}>
        Press <kbd>Enter</kbd> to send
      </div>
    </div>
  );
};

export default WhiteboardInput;
